import { Bed, BedKey, BedKeySchema, DELETION_FLAG, parse, t as tt } from "@packages/core";
import { useForm, useSelector } from "@tanstack/react-form";
import { Link, createFileRoute, useBlocker, useNavigate } from "@tanstack/react-router";
import { isEqual, pick } from "es-toolkit";
import { Fragment, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { LuImport, LuPlus, LuTrash } from "react-icons/lu";
import { toast } from "sonner";
import * as v from "valibot";

import {
  ButtonStyle2,
  Divider,
  FormField,
  FormFieldInput,
  FormFieldInputStyle,
  PageName,
  Spinner,
  Tooltip,
  TypographyStyle,
  iconButtonStyles,
} from "#components";
import {
  $alert,
  $confirm,
  modifyBed,
  registerBed,
  resetBedInfiniteQueries,
  useBedInfiniteQuery,
  useFacilityQuery,
} from "#services";
import { css } from "#styled-system/css";

export const Route = createFileRoute("/_authenticated/facilities/$facilityKey/beds/registration")({
  component: Component,
});

function Component() {
  const { facilityKey } = Route.useParams();

  const navigate = useNavigate();
  const { t } = useTranslation();

  const bedInfiniteQuery = useBedInfiniteQuery({
    index: "facilityKey-deleted-gsi",
    condition: [
      ["facilityKey", "=", facilityKey],
      ["deleted", "=", DELETION_FLAG.DEFAULT],
    ],
  });
  const facilityQuery = useFacilityQuery(facilityKey);

  useEffect(
    function () {
      // 有効なデータが対象の場合、全て取得する。
      if (bedInfiniteQuery.hasNextPage && !bedInfiniteQuery.isFetching) {
        bedInfiniteQuery.fetchNextPage();
      }
    },
    [bedInfiniteQuery],
  );

  const beds: Map<BedKey, Bed> = useMemo(
    function () {
      return (
        bedInfiniteQuery.data?.pages.reduce(function (beds, page) {
          for (const bed of page.beds) {
            beds.set(bed.bedKey, bed);
          }
          return beds;
        }, new Map()) ?? new Map()
      );
    },
    [bedInfiniteQuery.data],
  );

  const formSchema = useMemo(
    function () {
      return v.object({
        beds: v.array(
          v.object({
            /** ベッドキー */
            bedKey: v.optional(BedKeySchema()),
            /** ベッド名 */
            bedName: v.pipe(
              v.string(),
              v.trim(),
              v.maxGraphemes(32, function (issue) {
                return t("%TooManyCharacters.0%", { $: { maximum: issue.requirement } });
              }),
            ),
            /** ベッド発音 */
            bedPronunciation: v.pipe(
              v.string(),
              v.trim(),
              v.maxGraphemes(64, function (issue) {
                return t("%TooManyCharacters.0%", { $: { maximum: issue.requirement } });
              }),
            ),
            /** 居室名 */
            roomName: v.pipe(
              v.string(),
              v.trim(),
              v.minLength(1, t("%InputRequired.0%")),
              v.maxGraphemes(32, function (issue) {
                return t("%TooManyCharacters.0%", { $: { maximum: issue.requirement } });
              }),
            ),
            /** 居室発音 */
            roomPronunciation: v.pipe(
              v.string(),
              v.trim(),
              v.maxGraphemes(64, function (issue) {
                return t("%TooManyCharacters.0%", { $: { maximum: issue.requirement } });
              }),
            ),
          }),
        ),
      });
    },
    [t],
  );

  const form = useForm({
    defaultValues: useMemo(
      function (): v.InferInput<typeof formSchema> {
        const beds =
          bedInfiniteQuery.data?.pages
            .flatMap(function (page) {
              return page.beds;
            })
            .toSorted(function (a, b) {
              return (
                a.roomName.localeCompare(b.roomName) ||
                a.roomPronunciation.localeCompare(b.roomPronunciation) ||
                a.bedName.localeCompare(b.bedName) ||
                a.bedPronunciation.localeCompare(b.bedPronunciation) ||
                a.bedKey.localeCompare(b.bedKey)
              );
            }) ?? [];
        return {
          beds:
            beds.length > 0
              ? beds
              : [{ bedName: "", bedPronunciation: "", roomName: "", roomPronunciation: "" }],
        };
      },
      [bedInfiniteQuery.data],
    ),
    async onSubmit({ value: input }) {
      let output;
      try {
        output = parse(formSchema, input);
        console.debug("フォームが送信されました。", { input, output });
      } catch (error) {
        console.error("フォームの解析に失敗しました。", { input, output }, error);
        toast.error(t("%FormParsingFailed.0%"));
        return;
      }

      const toastId = toast.loading(
        tt({ ja: "ベッドを保存しています。" }) + " (" + 0 + " / " + output.beds.length + ")",
        { duration: Infinity },
      );

      const errorMessages: Array<string> = [];
      let numberOfSuccesses = 0;
      for (const [index, bed] of output.beds.entries()) {
        toast.loading(
          tt({ ja: "ベッドを保存しています。" }) +
            " (" +
            (index + 1) +
            " / " +
            output.beds.length +
            ")",
          { id: toastId },
        );

        try {
          if (bed.bedKey !== undefined) {
            if (isEqual(pick(beds.get(bed.bedKey) ?? Object.create(null), Object.keys(bed)), bed)) {
              console.debug("ベッド[" + bed.bedKey + "]は変更されていません。", bed);
            } else {
              console.debug("ベッド[" + bed.bedKey + "]を変更します。", bed);
              await modifyBed({ ...bed, bedKey: bed.bedKey });
            }
          } else {
            console.debug("ベッドを作成します。", bed);

            const {
              bed: { bedKey },
            } = await registerBed({ ...bed, facilityKey });

            form.setFieldValue(`beds[${index}]`, { ...bed, bedKey });
          }

          numberOfSuccesses++;
        } catch (error) {
          console.error("ベッドの登録/更新に失敗しました。", error);
          errorMessages.push(
            index + 1 + ". " + (error instanceof Error ? error.message : t("%Error.1%")),
          );
        }
      }

      if (errorMessages.length === 0) {
        resetBedInfiniteQueries();

        toast.success(
          tt({
            ja: "{{numberOfBeds}}床のベッドを保存しました。",
            _: { numberOfBeds: numberOfSuccesses.toLocaleString() },
          }),
          { duration: 5000, id: toastId },
        );

        navigate({
          ignoreBlocker: true,
          params: { facilityKey },
          to: "/facilities/$facilityKey/beds",
        });
      } else if (numberOfSuccesses === 0) {
        toast.dismiss(toastId);
        await $alert({
          color: "error",
          title: tt({ ja: "ベッドの保存に失敗" }),
          content:
            tt({ ja: "ベッドを保存出来ませんでした。" }) +
            "\n" +
            errorMessages.slice(0, 4).join("\n"),
        });
      } else {
        toast.dismiss(toastId);
        await $alert({
          color: "warning",
          title: tt({ ja: "一部のベッドの保存に失敗" }),
          content:
            tt({
              ja: "{{numberOfSuccessfulBeds}}床成功しましたが、{{numberOfFailedBeds}}床失敗しました。",
              _: {
                numberOfFailedBeds: errorMessages.length.toLocaleString(),
                numberOfSuccessfulBeds: numberOfSuccesses.toLocaleString(),
              },
            }) +
            "\n" +
            errorMessages.slice(0, 4).join("\n"),
        });
      }
    },
    onSubmitInvalid({ meta, value: input }) {
      console.warn("無効な入力", input, meta);
    },

    validators: { onSubmit: formSchema },
  });

  const formStoreStateIsSubmitting = useSelector(form.store, function (state) {
    return state.isSubmitting;
  });

  useBlocker({
    async shouldBlockFn() {
      return (
        (!form.store.state.isDefaultValue || form.store.state.isSubmitting) &&
        !(await $confirm({
          color: "warning",
          content: t("%Form.1%"),
          no: t("%Stay.0%"),
          title: t("%Form.0%"),
          yes: t("%Leave.0%"),
        }))
      );
    },
    withResolver: true,
  });

  return (
    <form
      className={css({
        flex: 1,
        display: "flex",
        flexDirection: "column",
        padding: { smDown: "16px", sm: "32px 24px" },
      })}
      data-main
      data-maximized
      onSubmit={function (event) {
        event.preventDefault();
        form.handleSubmit();
      }}
    >
      <PageName>
        {t("%BedInformation.0%") +
          " - " +
          (facilityQuery.data?.name ??
            (!facilityQuery.isFetching ? t("%Unknown.0%") : t("%Loading.0%")))}
      </PageName>

      <div
        className={css({
          alignItems: "flex-end",
          display: "flex",
          justifyContent: "flex-end",
        })}
      >
        <div
          className={css({
            alignItems: "center",
            display: "flex",
            gap: "8px",
          })}
        >
          <Link
            className={ButtonStyle2({ color: "white" })}
            disabled={formStoreStateIsSubmitting}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/beds/batch-registration"
          >
            <LuImport data-icon data-left />
            <span>{t("%Add.2%")}</span>
          </Link>

          <Divider length="28px" orientation="vertical" />

          <Link
            className={ButtonStyle2({ color: "white" })}
            disabled={formStoreStateIsSubmitting}
            onClick={function (event) {
              if (formStoreStateIsSubmitting) {
                event.preventDefault();
              }
            }}
            params={{ facilityKey }}
            to="/facilities/$facilityKey/beds"
          >
            {t("%Cancel.0%")}
          </Link>

          <button
            className={ButtonStyle2({ color: "black" })}
            disabled={formStoreStateIsSubmitting}
            type="submit"
          >
            {formStoreStateIsSubmitting && (
              <Spinner color="#FAFAFA" data-left size="14px" type="a" />
            )}
            {t("%Save.0%")}
          </button>
        </div>
      </div>

      <div
        className={css({
          backgroundColor: "white",
          borderRadius: "12px",
          flexGrow: 1,
          marginTop: { smDown: "12px", sm: "6px" },
          padding: { smDown: "16px", sm: "16px 24px" },
          minHeight: 0,
          overflow: "auto",
        })}
      >
        <h1
          className={TypographyStyle({
            fontSize: "18px",
            fontWeight: 700,
            lineHeight: "14 / 9",
          })}
        >
          {t("%BedInformation.0%")}
        </h1>

        <form.Field mode="array" name="beds">
          {function (field) {
            return (
              <Fragment>
                <div
                  className={css({
                    alignItems: "end",
                    display: "flex",
                    justifyContent: "end",
                    marginTop: "16px",
                  })}
                >
                  <div
                    className={css(
                      TypographyStyle.raw({
                        fontSize: "14px",
                        fontWeight: 500,
                        lineHeight: "10 / 7",
                      }),
                      {
                        alignItems: "center",
                        borderRadius: "6px",
                        cursor: "pointer",
                        display: "flex",
                        gap: "8px",
                        padding: "10px 16px",
                      },
                    )}
                    onClick={function () {
                      field.pushValue({
                        bedName: "",
                        bedPronunciation: "",
                        roomName: "",
                        roomPronunciation: "",
                      });
                    }}
                  >
                    <LuPlus className={css({ height: "16px", width: "auto" })} />
                    <span>{t("%Add.1%")}</span>
                  </div>
                </div>

                <div
                  className={css({
                    columnGap: "8px",
                    display: "grid",
                    gridTemplateColumns: {
                      smDown: "repeat(2, minmax(160px, 1fr)) auto",
                      sm: "repeat(4, minmax(160px, 1fr)) auto",
                    },
                    marginTop: "16px",
                    rowGap: "16px",
                    overflowX: "auto",
                  })}
                >
                  {field.state.value.map(function (_, index) {
                    return (
                      <div className={css({ display: "contents" })} key={index}>
                        <form.Field name={`beds[${index}].roomName`}>
                          {function (subfield) {
                            return (
                              <FormField
                                caution={subfield.state.meta.errors
                                  .filter(function (error) {
                                    return error !== undefined;
                                  })
                                  .map(function (error) {
                                    return error.message;
                                  })
                                  .join("")}
                                disabled={formStoreStateIsSubmitting}
                                invalid={subfield.state.meta.errors.length > 0}
                                label={t("%RoomName.0%")}
                              >
                                <FormFieldInput
                                  className={FormFieldInputStyle({ width: "stretch" })}
                                  id={subfield.name}
                                  onBlur={function () {
                                    subfield.handleBlur();
                                  }}
                                  onChange={function (event) {
                                    subfield.handleChange(event.target.value);
                                  }}
                                  value={subfield.state.value}
                                />
                              </FormField>
                            );
                          }}
                        </form.Field>
                        <form.Field name={`beds[${index}].roomPronunciation`}>
                          {function (subfield) {
                            return (
                              <FormField
                                caution={subfield.state.meta.errors
                                  .filter(function (error) {
                                    return error !== undefined;
                                  })
                                  .map(function (error) {
                                    return error.message;
                                  })
                                  .join("")}
                                data-hide-below="sm"
                                disabled={formStoreStateIsSubmitting}
                                invalid={subfield.state.meta.errors.length > 0}
                                label={t("%RoomPronunciation.1%")}
                              >
                                <FormFieldInput
                                  className={FormFieldInputStyle({ width: "stretch" })}
                                  id={subfield.name}
                                  onBlur={function () {
                                    subfield.handleBlur();
                                  }}
                                  onChange={function (event) {
                                    subfield.handleChange(event.target.value);
                                  }}
                                  value={subfield.state.value}
                                />
                              </FormField>
                            );
                          }}
                        </form.Field>
                        <form.Field name={`beds[${index}].bedName`}>
                          {function (subfield) {
                            return (
                              <FormField
                                caution={subfield.state.meta.errors
                                  .filter(function (error) {
                                    return error !== undefined;
                                  })
                                  .map(function (error) {
                                    return error.message;
                                  })
                                  .join("")}
                                disabled={formStoreStateIsSubmitting}
                                invalid={subfield.state.meta.errors.length > 0}
                                label={t("%BedName.0%")}
                              >
                                <FormFieldInput
                                  className={FormFieldInputStyle({ width: "stretch" })}
                                  id={subfield.name}
                                  onBlur={function () {
                                    subfield.handleBlur();
                                  }}
                                  onChange={function (event) {
                                    subfield.handleChange(event.target.value);
                                  }}
                                  value={subfield.state.value}
                                />
                              </FormField>
                            );
                          }}
                        </form.Field>
                        <form.Field name={`beds[${index}].bedPronunciation`}>
                          {function (subfield) {
                            return (
                              <FormField
                                caution={subfield.state.meta.errors
                                  .filter(function (error) {
                                    return error !== undefined;
                                  })
                                  .map(function (error) {
                                    return error.message;
                                  })
                                  .join("")}
                                data-hide-below="sm"
                                disabled={formStoreStateIsSubmitting}
                                invalid={subfield.state.meta.errors.length > 0}
                                label={t("%BedPronunciation.1%")}
                              >
                                <FormFieldInput
                                  className={FormFieldInputStyle({ width: "stretch" })}
                                  id={subfield.name}
                                  onBlur={function () {
                                    subfield.handleBlur();
                                  }}
                                  onChange={function (event) {
                                    subfield.handleChange(event.target.value);
                                  }}
                                  value={subfield.state.value}
                                />
                              </FormField>
                            );
                          }}
                        </form.Field>
                        <form.Field name={`beds[${index}].bedKey`}>
                          {function (subfield) {
                            return (
                              <FormField label="&nbsp;">
                                <Tooltip
                                  content={
                                    index === 0 || subfield.state.value !== undefined ? (
                                      <p className={TypographyStyle({})}>{t("%Undeletable.1%")}</p>
                                    ) : (
                                      <p className={TypographyStyle({})}>{t("%Undeletable.0%")}</p>
                                    )
                                  }
                                  trigger={
                                    <button
                                      className={iconButtonStyles({ color: "ghost", size: "40px" })}
                                      disabled={
                                        index === 0 ||
                                        subfield.state.value !== undefined ||
                                        formStoreStateIsSubmitting
                                      }
                                      onClick={function (event) {
                                        event.stopPropagation();
                                        event.preventDefault();
                                        field.handleChange(function (value) {
                                          return value.filter(function (_, i) {
                                            return i !== index;
                                          });
                                        });
                                      }}
                                    >
                                      <LuTrash />
                                    </button>
                                  }
                                />
                              </FormField>
                            );
                          }}
                        </form.Field>
                      </div>
                    );
                  })}
                </div>
              </Fragment>
            );
          }}
        </form.Field>
      </div>
    </form>
  );
}
